import React from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemText
} from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import AppsIcon from '@mui/icons-material/Apps';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Link, useLocation, useNavigate } from "react-router-dom";
import useMediaQuery from '@mui/material/useMediaQuery';

export default function TopMenu({ user, handleLogout }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [shortcutsAnchorEl, setShortcutsAnchorEl] = React.useState(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleShortcutsOpen = (event) => setShortcutsAnchorEl(event.currentTarget);
  const handleShortcutsClose = () => setShortcutsAnchorEl(null);

  const handleNav = (path) => {
    navigate(path);
    handleClose();
    handleShortcutsClose();
  };

  const publicMenu = [
    { label: "Accueil", path: "/" }
  ];

  const privateMenu = [
    { label: "Accueil", path: "/" },
    { label: "Espace prof", path: "/prof" },
    { label: "Applications", path: "/apps" },
    ...(isAdmin ? [{ label: "Administration", path: "/admin" }] : [])
  ];

  const shortcuts = [
    { label: "Classes", description: "Gérer les groupes et les élèves", path: "/classes" },
    { label: "Activités", description: "Définir activités et compétences", path: "/activites" },
    { label: "Suivi élèves", description: "Évaluer par classe et activité", path: "/suivi-eleves" },
    { label: "Applications", description: "Accéder aux applications élèves", path: "/apps" },
    ...(isAdmin ? [{ label: "Administration", description: "Gérer les comptes enseignants", path: "/admin" }] : [])
  ];

  const menuItems = user ? privateMenu : publicMenu;

  return (
    <AppBar position="static" color="primary" sx={{ mb: 2 }}>
      <Toolbar sx={{ minHeight: { xs: 68, sm: 72 } }}>
        <Typography
          variant="h6"
          sx={{ flexGrow: 1, cursor: "pointer", textDecoration: 'none', color: 'inherit', fontWeight: 800 }}
          onClick={() => navigate("/")}
        >
          My&apos;EPS
        </Typography>

        {isMobile ? (
          <>
            <IconButton color="inherit" onClick={handleMenu} size="large">
              <MenuIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
              {menuItems.map(item => (
                <MenuItem key={item.label} onClick={() => handleNav(item.path)} selected={location.pathname === item.path}>
                  {item.label}
                </MenuItem>
              ))}

              {user && <Divider />}

              {user && shortcuts.map(item => (
                <MenuItem key={item.label} onClick={() => handleNav(item.path)}>
                  <ListItemText primary={item.label} secondary={item.description} />
                </MenuItem>
              ))}

              {!user ? (
                <MenuItem component={Link} to="/login" onClick={handleClose}>
                  <LoginOutlinedIcon sx={{ mr: 1 }} /> Connexion
                </MenuItem>
              ) : (
                <MenuItem onClick={() => { handleClose(); handleLogout && handleLogout(); navigate('/'); }}>
                  <LogoutIcon sx={{ mr: 1 }} /> Déconnexion
                </MenuItem>
              )}
            </Menu>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {menuItems.map(item => (
              <Button
                key={item.label}
                color="inherit"
                component={Link}
                to={item.path}
                startIcon={
                  item.path === '/prof'
                    ? <DashboardRoundedIcon />
                    : item.path === '/apps'
                      ? <AppsIcon />
                      : item.path === '/admin'
                        ? <AdminPanelSettingsRoundedIcon />
                        : undefined
                }
                sx={{
                  mx: 0.5,
                  textTransform: 'none',
                  borderRadius: 999,
                  px: 2,
                  minHeight: 44,
                  backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.16)' : 'transparent'
                }}
              >
                {item.label}
              </Button>
            ))}

            {user && (
              <>
                <Button
                  color="inherit"
                  onClick={handleShortcutsOpen}
                  endIcon={<KeyboardArrowDownRoundedIcon />}
                  sx={{ textTransform: 'none', borderRadius: 999, px: 2, minHeight: 44 }}
                >
                  Accès rapides
                </Button>
                <Menu
                  anchorEl={shortcutsAnchorEl}
                  open={Boolean(shortcutsAnchorEl)}
                  onClose={handleShortcutsClose}
                >
                  {shortcuts.map(item => (
                    <MenuItem key={item.label} onClick={() => handleNav(item.path)}>
                      <ListItemText primary={item.label} secondary={item.description} />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {!user ? (
              <Button
                variant="contained"
                color="secondary"
                component={Link}
                to="/login"
                startIcon={<LoginOutlinedIcon />}
                sx={{ ml: 2, textTransform: 'none', minHeight: 44 }}
              >
                Connexion
              </Button>
            ) : (
              <Button
                color="inherit"
                onClick={() => { handleLogout && handleLogout(); navigate('/'); }}
                startIcon={<LogoutIcon />}
                sx={{ ml: 1, textTransform: 'none', minHeight: 44 }}
              >
                Déconnexion
              </Button>
            )}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}